/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1734393600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Dec 17 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "marigold-engage-plus",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported content",
          "depth": 0,
          "id": "supported-content"
        }, {
          "value": "Connect to Marigold Engage+",
          "depth": 0,
          "id": "connect-to-marigold-engage"
        }, {
          "value": "Advanced configuration",
          "depth": 0,
          "id": "advanced-configuration"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Form IDs",
          "depth": 1,
          "id": "form-ids"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      ol: "ol",
      li: "li",
      h3: "h3"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-content",
        children: "Supported content"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Sync records from your model to audiences in real-time or bulk"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Marigold Engage+ supports both real-time and bulk sync modes.\nReal-time syncs are recommended only for small data sets,\nwhile bulk syncs are recommended for any data set over 1 million."
        })
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-marigold-engage",
        children: "Connect to Marigold Engage+"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Marigold Engage+"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Marigold Engage+"
        }), " with help from your Marigold agent."]
      }), "\n", _jsx(_components.p, {
        children: "Then in Hightouch:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Enter the Customer ID that your Marigold agent provides"
        }), "\n", _jsx(_components.li, {
          children: "Enter the region your Marigold account is in"
        }), "\n", _jsx(_components.li, {
          children: "Enter the API and Secret keys from Marigold"
        }), "\n", _jsx(_components.li, {
          children: "If you would like to sync in bulk, enter the SFTP credentials your Marigold agent provides"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "advanced-configuration",
        children: "Advanced configuration"
      }), "\n", _jsx(_components.p, {
        children: "If you are syncing in real-time to joined tables you must specify which fields should be sent to which forms in the destination config."
      }), "\n", _jsx(_components.p, {
        children: "If you are syncing in bulk you must specify the available mapping options in the destination config.\nYour Marigold agent will help you configure the available options for your use case."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Marigold Engage+ destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Marigold Engage+ destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Field mapping is where you select which customer information parameters or other record parameters you want to send to Marigold.\nYou can map data from any of your model columns to the list's preconfigured fields."
      }), "\n", _jsx(_components.h3, {
        id: "form-ids",
        children: "Form IDs"
      }), "\n", _jsx(_components.p, {
        children: "When syncing in real-time mode you must also create an API post form in Marigold and enter the form's ID in Hightouch."
      }), "\n", _jsx(_components.p, {
        children: "The form will control which campaigns are triggered as Highouch sends data to Marigold."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
